import type { DragEvent } from '../CanvasEvent';
import CanvasObject from '../CanvasObject';
import type SurfaceBase from '../SurfaceBase';
import type { Point } from '../SurfaceBase';
import { BBOX_COLOR, getMidpoint, OPPOSITE_INDEX } from './common';
import ResizeHandle from './ResizeHandle';
import RotateHandle from './RotateHandle';

export interface ScaleState {
  // the handle point which is opposite of the handle being dragged
  origin: Point;
  // original hypotenuse length from corner to corner
  originalHypot: number;
  // original width and height of the rotated bbox
  originalWidth: number;
  originalLength: number;
  // angle from diagonal line connecting the corners to (0,0) (uses Math.atan2())
  angle: number;
  // when dragging a handle, it affects two other resize handle points, those are tracked here.
  widthAngle: number;
  heightAngle: number;
}

export default class BBoxObject extends CanvasObject {
  parent: CanvasObject;
  rotation: number = 0;
  resizeHandles: ResizeHandle[] = [];

  isDragging: boolean = false;
  dragOffset: Point = { x: 0, y: 0 };

  rotateHandle: RotateHandle;

  scaleState: ScaleState = {
    origin: { x: NaN, y: NaN },
    originalHypot: NaN,
    originalWidth: NaN,
    originalLength: NaN,
    angle: 0,
    widthAngle: 0,
    heightAngle: 0,
  };

  constructor(id: string, surface: SurfaceBase, parent: CanvasObject) {
    super(id, surface);
    this.interactive = true;
    this.parent = parent;

    const coords = this.parent.getBBoxCoords();
    coords.forEach((c, i) => {
      // this.addChild(new RotateHandle(`rotate-${i}`, this.surface, this, i));
      const r = new ResizeHandle(`resize-${i}`, this.surface, {
        point: c,
        index: i,
        bbox: this,
      });

      r.on('dragStart', (e) => {
        this.emit('scaleStart', e, i);
      });

      r.on('dragging', (e) => {
        this.emit('scaling', e, i);
      });

      r.on('dragEnd', (e) => {
        this.emit('scaleEnd');
      });

      this.resizeHandles.push(r);
      this.addChild(r);
    });

    const midpoint = getMidpoint(coords[0], coords[1]);
    this.rotateHandle = new RotateHandle(`rotate-${this.id}`, this.surface, {
      midpoint,
      bbox: this,
    });

    this.addChild(this.rotateHandle);

    this.on('dragStart', (e) => {
      this.setResizeHandlePointsOnDrag();
      this.rotateHandle.refreshScreenCoords();
    });

    this.on('dragging', (e) => {
      this.setResizeHandlePointsOnDrag();
      this.rotateHandle.refreshScreenCoords();
    });

    this.on('dragEnd', (e) => {
      this.setResizeHandlePointsOnDrag();
      this.resizeHandles.forEach((r, i) => {
        r.refreshMapCoords();
      });
      this.rotateHandle.refreshScreenCoords();
      this.rotateHandle.refreshMapCoords();
    });

    this.on('scaleStart', (e, i) => {
      const oppIndex = OPPOSITE_INDEX[i];
      const origin = this.resizeHandles[oppIndex].point;

      const p = this.resizeHandles[i].point;

      const originalHypot = Math.hypot(origin.x - p.x, origin.y - p.y);
      const quandrant = this.getQuandrantRelativeToOrigin(p, origin);
      const angle = this.calculateLineAngle(p, origin, quandrant);

      const p1 = this.resizeHandles[0].point;
      const p2 = this.resizeHandles[1].point;
      const p3 = this.resizeHandles[2].point;

      const originalWidth = Math.hypot(p2.x - p1.x, p2.y - p1.y);
      const originalLength = Math.hypot(p3.x - p2.x, p3.y - p2.y);

      const { widthAngle, heightAngle } = this.calculateWidthAndHeightAngles(i);

      this.scaleState = {
        origin,
        originalHypot,
        originalWidth,
        originalLength,
        angle,
        widthAngle,
        heightAngle,
      };

      this.handleScale(e, i);
      this.parent.emit('scaleStart', e, i);
      this.rotateHandle.emit('scaleStart', e, i);
    });

    this.on('scaling', (e, i) => {
      this.handleScale(e, i);
      this.parent.emit('scaling', e, i);
      this.rotateHandle.emit('scaling', e, i);
      this.renderAll();
    });

    this.on('scaleEnd', () => {
      this.parent.emit('scaleEnd');
      const coords = this.parent.getBBoxCoords();
      this.resizeHandles.forEach((r, i) => {
        r.setPoint(coords[i]);
        r.refreshMapCoords();
      });
      this.rotateHandle.emit('scaleEnd');
      this.renderAll();
    });

    this.on('rotateStart', () => {
      // don't need to do anything here
      this.parent.emit('rotateStart');
    });

    this.on('rotating', (e) => {
      this.parent.emit('rotating', e);
      const coords = this.parent.getBBoxCoords();
      this.resizeHandles.forEach((r, i) => {
        r.setPoint(coords[i]);
      });
    });

    this.on('rotateEnd', () => {
      this.parent.emit('rotateEnd');
      const coords = this.parent.getBBoxCoords();
      this.resizeHandles.forEach((r, i) => {
        r.setPoint(coords[i]);
        r.refreshMapCoords();
      });
    });
  }

  setResizeHandlePointsOnDrag = () => {
    const coords = this.parent.getBBoxCoords();
    this.resizeHandles.forEach((r, i) => {
      r.setPoint(coords[i]);
    });
  };

  calculateWidthAndHeightAngles = (i: number): { widthAngle: number; heightAngle: number } => {
    const getResizeHandlePoints = (i: number): [Point, Point] => {
      if (i === 0) {
        return [this.resizeHandles[1].point, this.resizeHandles[3].point];
      } else if (i === 1) {
        return [this.resizeHandles[0].point, this.resizeHandles[2].point];
      } else if (i === 2) {
        return [this.resizeHandles[3].point, this.resizeHandles[1].point];
      }

      return [this.resizeHandles[2].point, this.resizeHandles[0].point];
    };

    const p = this.resizeHandles[i].point;
    const [p1, p2] = getResizeHandlePoints(i);

    const dAx = p1.x - p.x;
    const dAy = p1.y - p.y;

    const widthAngle = Math.atan2(dAy, dAx);

    const dBx = p2.x - p.x;
    const dBy = p2.y - p.y;

    const heightAngle = Math.atan2(dBx, dBy);

    return { widthAngle, heightAngle };
  };

  getQuandrantRelativeToOrigin = (p: Point, o: Point): number => {
    if (p.x <= o.x && p.y <= o.y) {
      return 1;
    } else if (p.x >= o.x && p.y <= o.y) {
      return 2;
    } else if (p.x >= o.x && p.y >= o.y) {
      return 3;
    }

    return 4;
  };

  // takes resize handle original point, origin and quandrant number
  calculateLineAngle = (p: Point, o: Point, q: number): number => {
    const dx = p.x - o.x;
    const dy = p.y - o.y;
    return Math.atan2(dx, dy);
  };

  // "i" is the incoming index from the resize handle being dragged.
  // "i" can be a value of 0-3.
  handleScale = (e: DragEvent, i: number) => {
    const r = this.resizeHandles[i];
    const oppIndex = OPPOSITE_INDEX[i];
    const opp = this.resizeHandles[oppIndex].point;

    const { origin, originalHypot, originalWidth, originalLength, angle, widthAngle, heightAngle } = this.scaleState;

    // get current cursor point
    const cp = e.point;

    // get total hypotenuse length from cursor to opposite point (origin)
    const dt = Math.hypot(cp.x - opp.x, cp.y - opp.y);

    // dx and dy from cursor to origin (opposite)
    const dx = dt * Math.sin(angle);
    const dy = dt * Math.cos(angle);

    // calculate where the new resize handle should be
    const point: Point = {
      x: origin.x + dx,
      y: origin.y + dy,
    };

    r.setPoint(point);

    // get current scale (new hypot over original)
    const scale = dt / originalHypot;

    // get scaled with and height
    const w = originalWidth * scale;
    const h = originalLength * scale;

    // current resize handle point
    const p = r.point;

    const dAx = w * Math.cos(widthAngle);
    const dAy = w * Math.sin(widthAngle);

    // point for first affected resize handle.
    const p1: Point = {
      x: p.x + dAx,
      y: p.y + dAy,
    };

    const dBx = h * Math.sin(heightAngle);
    const dBy = h * Math.cos(heightAngle);

    // point for second affected resize handle.
    const p2: Point = {
      x: p.x + dBx,
      y: p.y + dBy,
    };

    // set applicable points
    if (i === 0) {
      this.resizeHandles[1].setPoint(p1);
      this.resizeHandles[3].setPoint(p2);
    } else if (i === 1) {
      this.resizeHandles[0].setPoint(p1);
      this.resizeHandles[2].setPoint(p2);
    } else if (i === 2) {
      this.resizeHandles[3].setPoint(p1);
      this.resizeHandles[1].setPoint(p2);
    } else if (i === 3) {
      this.resizeHandles[2].setPoint(p1);
      this.resizeHandles[0].setPoint(p2);
    }
  };

  getBBoxCoords = (): Point[] => {
    const p1 = this.resizeHandles[0].point;
    const p2 = this.resizeHandles[1].point;
    const p3 = this.resizeHandles[2].point;
    const p4 = this.resizeHandles[3].point;
    return [p1, p2, p3, p4];
  };

  getCoordsAtIndex = (index: number) => {
    const coords = this.parent.getBBoxCoords();
    return coords[index];
  };

  getCenter = (): Point => {
    const p1 = this.resizeHandles[0].point;
    const p2 = this.resizeHandles[2].point;

    const c: Point = {
      x: (p1.x + p2.x) / 2,
      y: (p1.y + p2.y) / 2,
    };

    return c;
  };

  render(
    ctx: CanvasRenderingContext2D,
    strokeColor?: string | undefined,
    strokeWidth?: number | undefined,
    isDashed?: boolean | undefined,
    strokeOpacity?: number | undefined,
    isBorderHidden?: boolean | undefined,
  ): void {
    if (this.resizeHandles.length === 0) {
      throw new Error(`BBox has no resize handles`);
    }

    const points = this.resizeHandles.map((r) => r.point);

    const p1 = points[0];

    ctx.save();
    ctx.lineWidth = 1;
    ctx.strokeStyle = BBOX_COLOR;
    ctx.beginPath();
    ctx.moveTo(p1.x, p1.y);

    for (let i = 1; i < points.length; i++) {
      const p = points[i];
      ctx.lineTo(p.x, p.y);
    }

    ctx.closePath();
    ctx.stroke();
    ctx.restore();

    // image border
    if (isBorderHidden || isBorderHidden === undefined) return;
    ctx.globalCompositeOperation = 'destination-over';
    ctx.save();
    ctx.strokeStyle = strokeColor ?? '#000000';
    ctx.globalAlpha = strokeOpacity ?? 1;
    ctx.lineWidth = strokeWidth ?? 4;
    isDashed ? ctx.setLineDash([10, 4]) : ctx.setLineDash([]);

    ctx.beginPath();
    ctx.moveTo(p1.x, p1.y);

    for (let i = 1; i < points.length; i++) {
      const p = points[i];
      ctx.lineTo(p.x, p.y);
    }

    ctx.closePath();
    ctx.stroke();
    ctx.restore();
  }

  drawHitArea(ctx: OffscreenCanvasRenderingContext2D | CanvasRenderingContext2D, color: string): void {
    // no hit area since we are not drawing anything for the BBox itself.
  }
}
